import { FC, useMemo } from 'react';

import { AssignedUnitShiftStatus, IAssignedUnitShift } from '@/models';
import { canModifyCFS } from '@/services';
import { useAgencyStore, useUserStore } from '@/store';
import { colors } from '@/theme/variables';

import { UnitItem } from './UnitItem';
import { ActionButton, ActionButtonItem } from '../../../Buttons';
import { unitShiftReassignActionItems, unitShiftStatusItems } from '../data';

interface AssignedUnitsProps extends IAssignedUnitShift {
  cfsNumber?: string;
  reassign?: boolean;
  unitShiftActionItems: ActionButtonItem[];
  addressLabel?: string;
  distance?: number;
  handleSelectAction: (value: string | number, reassign?: boolean) => void;
  isCfsClosed?: boolean;
}

export const AssignedUnitItem: FC<AssignedUnitsProps> = (props) => {
  const {
    status,
    unitShift,
    reassign,
    unitShiftActionItems,
    handleSelectAction,
    isCfsClosed,
  } = props;

  const { agencies } = useAgencyStore();
  const { user } = useUserStore();

  const assignedUnitShiftStatusItems = useMemo(() => {
    let items = unitShiftStatusItems.slice();
    if (status === AssignedUnitShiftStatus.QUEUED) {
      items = items.map((item) => ({ ...item, disabled: true }));
      items.push({
        label: 'Queued',
        value: AssignedUnitShiftStatus.QUEUED,
      });
    }
    return items;
  }, [status]);

  return (
    <UnitItem
      {...props}
      {...unitShift?.unit}
      agency={agencies.find((agency) => agency._id === unitShift?.unit?.agency)}
      rootProps={{
        sx: {
          borderLeft: `1px solid ${colors.error.main}`,
        },
      }}
      resources={{
        users: unitShift?.users,
        equipments: unitShift?.equipments,
        vehicles: unitShift?.vehicles,
        polygons: unitShift?.polygons,
      }}
      showResources
      showReallocateButton
      unitAgency={unitShift?.unit.agency}
    >
      {canModifyCFS(user) && (
        <>
          <ActionButton
            color="inherit"
            variant="contained"
            defaultValue={status}
            items={assignedUnitShiftStatusItems}
            onSelectItem={handleSelectAction}
            disabled={isCfsClosed}
            size="small"
          />
          <ActionButton
            size="small"
            color="error"
            variant="contained"
            disabled={reassign === false || isCfsClosed}
            items={
              reassign ? unitShiftReassignActionItems : unitShiftActionItems
            }
            onSelectItem={(v) => handleSelectAction(v, reassign)}
          >
            {reassign ? 'Reassign to CFS' : 'More actions'}
          </ActionButton>
        </>
      )}
    </UnitItem>
  );
};
